import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./imageCarousel.module.scss";

const ImageCarousel = ({ title, images = [], disabled = false, imageWidth = 180, spaceBetween = 8, autoplay = true }) => {
  const [needsSliding, setNeedsSliding] = useState(false);
  const [visibleCount, setVisibleCount] = useState(1);

  const [internalIndex, setInternalIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const autoplayRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const isTransitioningRef = useRef(false);

  const totalSlides = images.length;

  useEffect(() => {
    if (!viewportRef.current?.offsetWidth) return;

    const measure = () => {
      if (!viewportRef.current) return;
      const containerWidth = viewportRef.current.offsetWidth;
      const totalContentWidth = totalSlides * imageWidth + (totalSlides - 1) * spaceBetween;

      const slides = totalContentWidth > containerWidth;
      setNeedsSliding(slides);

      const count = Math.floor((containerWidth + spaceBetween) / (imageWidth + spaceBetween));
      setVisibleCount(Math.max(1, count));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewportRef.current);
    return () => ro.disconnect();
  }, [totalSlides, imageWidth, spaceBetween]);

  useEffect(() => {
    if (!needsSliding) {
      setInternalIndex(0);
      return;
    }
    setInternalIndex(visibleCount);
  }, [needsSliding, visibleCount]);

  const clonedSlides = needsSliding
    ? [
        ...images.slice(-visibleCount).map((img, i) => ({
          img,
          key: `clone-start-${i}`,
        })),
        ...images.map((img, i) => ({ img, key: `real-${i}` })),
        ...images.slice(0, visibleCount).map((img, i) => ({
          img,
          key: `clone-end-${i}`,
        })),
      ]
    : images.map((img, i) => ({ img, key: `real-${i}` }));

  const realIndex = needsSliding ? (((internalIndex - visibleCount) % totalSlides) + totalSlides) % totalSlides : 0;

  const goToInternal = useCallback((nextInternal, withTransition = true) => {
    if (isTransitioningRef.current) return;

    if (withTransition) {
      isTransitioningRef.current = true;
      setIsTransitioning(true);
    }
    setInternalIndex(nextInternal);
  }, []);

  const goNext = useCallback(() => goToInternal(internalIndex + 1), [internalIndex, goToInternal]);

  const goPrev = useCallback(() => goToInternal(internalIndex - 1), [internalIndex, goToInternal]);

  const handleTransitionEnd = useCallback(() => {
    isTransitioningRef.current = false;
    setIsTransitioning(false);

    const firstReal = visibleCount;
    const lastReal = visibleCount + totalSlides - 1;

    if (internalIndex < firstReal) {
      const jump = internalIndex + totalSlides;
      setInternalIndex(jump);
    } else if (internalIndex > lastReal) {
      const jump = internalIndex - totalSlides;
      setInternalIndex(jump);
    }
  }, [internalIndex, visibleCount, totalSlides]);

  const goToReal = useCallback(
    (realIdx) => {
      goToInternal(realIdx + visibleCount);
    },
    [visibleCount, goToInternal]
  );

  useEffect(() => {
    clearInterval(autoplayRef.current);
    if (disabled || !autoplay || !needsSliding || totalSlides <= 1) return;
    autoplayRef.current = setInterval(goNext, 3000);
    return () => clearInterval(autoplayRef.current);
  }, [disabled, autoplay, needsSliding, goNext, totalSlides]);

  if (!images.length) return null;

  const translateX = -(internalIndex * (imageWidth + spaceBetween));

  return (
    <div className={styles.sliderContainer}>
      {title && <h3>{title}</h3>}
      <div className={styles.carouselOuter}>
        {!disabled && needsSliding && (
          <button className={`${styles.navBtn} ${styles.navBtnPrev}`} onClick={goPrev} aria-label="Previous slide" data-testid="carousel-prev">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="15 18 9 12 15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div ref={viewportRef} className={styles.carouselViewport}>
          <div
            ref={trackRef}
            className={styles.carouselTrack}
            data-testid="carousel-track"
            onTransitionEnd={needsSliding ? handleTransitionEnd : undefined}
            style={{
              transform: `translateX(${translateX}px)`,
              gap: `${spaceBetween}px`,
              transition: isTransitioning ? undefined : "none",
            }}
          >
            {clonedSlides.map(({ img, key }, i) => (
              <div key={key} className={styles.slide} style={{ minWidth: imageWidth, width: imageWidth }}>
                <div className={styles.imageWrapper} style={{ width: imageWidth, height: imageWidth }}>
                  <img
                    src={img}
                    alt={key}
                    loading="lazy"
                    data-disabled={disabled}
                    className={disabled ? styles.disable_image : styles.enable_image}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {!disabled && needsSliding && (
          <button className={`${styles.navBtn} ${styles.navBtnNext}`} onClick={goNext} aria-label="Next slide" data-testid="carousel-next">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="9 18 15 12 9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {needsSliding && (
          <div className={styles.pagination} data-testid="carousel-pagination">
            {images.map((_, i) => (
              <button
                key={i}
                className={`${styles.dot} ${i === realIndex ? styles.dotActive : ""}`}
                onClick={() => goToReal(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-pressed={i === realIndex}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageCarousel;
